import React, {
    ChangeEvent,
    HTMLInputAutoCompleteAttribute,
    HTMLInputTypeAttribute
} from "react";
import {
    ControllerRenderProps,
    FieldValues,
    Path,
    useWatch
} from "react-hook-form";
import { withMask } from "use-mask-input";

import { Input } from "@/components/Form/Base/input.component";
import { InputMask } from "@/components/Form/Base/maskedInput.component";
import { getFieldValue } from "@/components/Form/RHF/Utils/form.helpers";

type MaskedPrefixedInputPartialProps<T extends FieldValues> = {
    prefixName: Path<T>;
    field: ControllerRenderProps<T, Path<T>>;
    onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
    type?: HTMLInputTypeAttribute;
    placeholder?: string;
    autoComplete?: HTMLInputAutoCompleteAttribute;
    disabled?: boolean;
    isValid: boolean;
    isInvalid: boolean;
    isLoading?: boolean;
    onAutofill?: (event: ChangeEvent<HTMLInputElement>) => void;
    inputMaskBasedOnPrefix: (value: string) => InputMask;
};

export const MaskedPrefixInput = <T extends FieldValues>({
    prefixName,
    field,
    onPaste,
    type = "text",
    placeholder,
    autoComplete,
    disabled,
    isValid,
    isInvalid,
    isLoading,
    onAutofill,
    inputMaskBasedOnPrefix
}: MaskedPrefixedInputPartialProps<T>) => {
    const prefix = useWatch<T>({ name: prefixName });

    const { mask, ...maskOptions } = inputMaskBasedOnPrefix(prefix);

    return (
        <Input
            {...field}
            value={getFieldValue(field, !!isLoading, "")}
            onPaste={(e) => onPaste?.(e)}
            id={field.name}
            ref={(ref) => {
                withMask(mask, { ...maskOptions })(ref);
                field.ref(ref);
            }}
            className="prefixed-input__input"
            type={type}
            placeholder={mask ? "" : placeholder}
            autoComplete={autoComplete}
            valid={isValid}
            invalid={isInvalid}
            disabled={disabled}
            onAutofill={onAutofill}
        />
    );
};
